import { useState, useCallback, useEffect, useRef, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import type { StoryDetail as StoryDetailType, ArticleChange } from '../types';
import { stripHtml, sanitizeHtml, decodeHtmlEntities } from '../utils/html';
import { formatDate, formatRelativeTime } from '../utils/formatTime';
import { linkHost } from '../utils/linkHost';
import { isLinkWrapperStory, readerLinkUrl } from '../utils/storyLinks';
import { STATUS_THEME_COLORS } from '../utils/statusColors';
import { ArticleLifecycle } from './ArticleLifecycle';
import { ModalOverlay } from './ModalOverlay';
import { ArticleNotePanel, IconNote } from './ArticleNoteWidget';
import { ListPicker, IconList } from './ListPicker';
import type { ArticleNotePanelHandle } from './ArticleNoteWidget';
import { CopyButton } from './CopyButton';
import { IconExternalLink, IconPencil } from './icons';
import { BookmarkToggleButton } from './BookmarkToggleButton';
import { AdminScoreBadge } from './AdminScoreBadge';
import { GatedMarker } from './GatedMarker';
import { MediaKindMarker } from './MediaKindMarker';
import { shortcutKeyMatches } from '../utils/keyboard';
import * as adminStoryRepository from '../repositories/adminStoryRepository';
import { recordEvent } from '../repositories/storyRepository';
import { safeHref } from '../utils/html';

interface StoryDetailProps {
  story: StoryDetailType | null;
  loading: boolean;
  isAdmin: boolean;
  onToggleRead: () => void;
  onToggleFlag: () => void;
  onFetch?: () => Promise<void>;
  onEnrich?: () => Promise<void>;
  onSummaryUpdate?: (newSummary: string) => void;
  onTopicUpdate?: (newTopic: string) => void;
  focused?: boolean;
}

function IconRead({ filled }: { filled: boolean }) {
  return filled ? (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9"/>
    </svg>
  ) : (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9"/>
    </svg>
  );
}

function IconInspect() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="16 18 22 12 16 6"/>
      <polyline points="8 6 2 12 8 18"/>
    </svg>
  );
}

function InspectModal({ title, html, onClose }: { title: string; html: string; onClose: () => void }) {
  return (
    <ModalOverlay onClose={onClose} className="inspect-html-modal">
        <div className="inspect-header">
          <h2>Raw HTML — {title}</h2>
          <button className="pane-action-btn" onClick={onClose} title="Close">✕</button>
        </div>
        <pre className="inspect-html-pre">{html || '(empty)'}</pre>
    </ModalOverlay>
  );
}

export const StoryDetail = forwardRef<HTMLElement, StoryDetailProps>(function StoryDetail(
  { story, loading, isAdmin, onToggleRead, onToggleFlag, onFetch, onEnrich, onSummaryUpdate, onTopicUpdate, focused },
  ref,
) {
  const [inspectModal, setInspectModal] = useState<'summary' | 'content' | null>(null);
  const [storyTier, setStoryTier] = useState<number | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteHasContent, setNoteHasContent] = useState(false);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const handleNoteHasContent = useCallback((has: boolean) => setNoteHasContent(has), []);
  const notePanelRef = useRef<ArticleNotePanelHandle>(null);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  const [changes, setChanges] = useState<ArticleChange[]>([]);
  const [changesOpen, setChangesOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(false);
  const [topicDraft, setTopicDraft] = useState('');
  const [savingTopic, setSavingTopic] = useState(false);
  const topicInputRef = useRef<HTMLInputElement>(null);
  const listBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setNoteOpen(false);
    setNoteHasContent(false);
    setListPickerOpen(false);
    setEditingSummary(false);
    setChanges([]);
    setChangesOpen(false);
    setStoryTier(story?.tier ?? null);
  }, [story?.id]);

  useEffect(() => {
    if (!isAdmin || !story?.id) return;
    adminStoryRepository.getStoryChanges(story.id)
      .then(setChanges)
      .catch(() => {});
  }, [isAdmin, story?.id]);

  const focusedClass = focused ? ' story-detail-pane--focused' : '';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      const btn = document.querySelector<HTMLElement>('.story-row.active .story-row-button');
      btn?.focus();
      btn?.closest('.story-row')?.scrollIntoView({ block: 'nearest' });
      return;
    }
    const target = e.target as HTMLElement;
    const inTextInput = target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable;
    if (shortcutKeyMatches(e.nativeEvent, 'n') && !inTextInput && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      setNoteOpen((v) => {
        const opening = !v;
        if (opening) {
          // focus at end after state update + render
          requestAnimationFrame(() => notePanelRef.current?.focusAtEnd());
        }
        return opening;
      });
    }
  };

  if (loading) {
    return <section ref={ref} tabIndex={-1} className={`story-detail-pane${focusedClass}`} onKeyDown={handleKeyDown}><div className="empty-panel detail-empty">Loading...</div></section>;
  }

  if (!story) {
    return <section ref={ref} tabIndex={-1} className={`story-detail-pane${focusedClass}`} onKeyDown={handleKeyDown}><div className="empty-panel detail-empty">Select an article to read.</div></section>;
  }

  const summaryText = stripHtml(story.summary || '');
  const contentHtml = sanitizeHtml(story.content || story.summary || '');

  return (
    <section ref={ref} tabIndex={-1} className={`story-detail-pane${focusedClass}`} onKeyDown={handleKeyDown}>
      <div className="story-detail">
        <div className="story-detail-header">
          <h2>
            {decodeHtmlEntities(story.title) || '(Untitled)'}
            <GatedMarker isGated={story.is_gated} />
            <MediaKindMarker kind={story.media_kind} />
            {readerLinkUrl(story) && (
              <a className="title-external-link" href={safeHref(readerLinkUrl(story)!)} target="_blank" rel="noreferrer" title="Open original" onClick={() => recordEvent(story.id, 'click_external')}>
                <IconExternalLink size={13} strokeWidth={2.5} />
              </a>
            )}
          </h2>
          <div className="detail-meta-row">
            <div className="detail-status-icons">
              <button
                className={`status-icon-btn${story.is_read ? ' read' : ''}`}
                onClick={onToggleRead}
                title={story.is_read ? 'Mark unread' : 'Mark read'}
              >
                <IconRead filled={!story.is_read} />
              </button>
              <BookmarkToggleButton
                isFlagged={story.is_flagged}
                onClick={() => onToggleFlag()}
                className="status-icon-btn"
              />
              <button
                className={`status-icon-btn${noteOpen ? ' note-open' : ''}${noteHasContent && !noteOpen ? ' note-has-content' : ''}`}
                onClick={() => setNoteOpen((v) => !v)}
                title={noteOpen ? 'Close note' : 'Open note'}
              >
                <IconNote hasContent={noteHasContent} />
              </button>
              <div className="list-picker-container">
                <button
                  ref={listBtnRef}
                  className={`status-icon-btn${listPickerOpen ? ' note-open' : ''}`}
                  onClick={() => setListPickerOpen((v) => !v)}
                  title="Add to list"
                >
                  <IconList />
                </button>
                <ListPicker
                  articleId={story.id}
                  open={listPickerOpen}
                  onClose={() => setListPickerOpen(false)}
                  triggerRef={listBtnRef}
                />
              </div>
            </div>
            {story.author && (
              <span className="detail-author">{story.author}</span>
            )}
            <div className="detail-meta">
              {story.canonical_domain && <span>{story.canonical_domain}</span>}
              {story.published_at && <span>{formatDate(story.published_at)}</span>}
              {story.category && <span>{story.category}</span>}
              {story.estimated_reading_minutes != null && <span className="detail-reading-time">{story.estimated_reading_minutes} min read</span>}
              {isAdmin && (
                <span className="detail-score-group">
                  <AdminScoreBadge
                    articleId={story.id}
                    score={storyTier ?? story.tier ?? null}
                    label="Q"
                    onUpdate={(_id, newScore) => setStoryTier(newScore)}
                  />
                </span>
              )}
            </div>
          </div>
          <ArticleNotePanel
            ref={notePanelRef}
            articleId={story.id}
            onHasContent={handleNoteHasContent}
            onDeleted={() => setNoteOpen(false)}
            hidden={!noteOpen}
          />
        </div>

        {isAdmin && (
          <div className="admin-toolbar">
            <a className="admin-icon-link" href={`/admin/article/${story.id}`} target="_blank" rel="noreferrer" title="Article admin">
              <IconExternalLink size={11} strokeWidth={2.5} />
            </a>
            {story.site_id && (
              <a className="admin-icon-link" href={`/sites/${story.site_id}`} target="_blank" rel="noreferrer" title="Site admin">
                <IconExternalLink size={11} strokeWidth={2.5} />
              </a>
            )}
            <ArticleLifecycle
              hasFetched={!!story.content}
              hasEnriched={!!story.scoring_completed_at}
              isQueued={!!story.enrichment_queued_at && !story.scoring_completed_at}
              canFetch={!!story.url && !isLinkWrapperStory(story)}
              onFetch={onFetch}
              onEnrich={onEnrich}
            />
            {editingTopic ? (
              <form
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const trimmed = topicDraft.trim();
                  if (!trimmed || trimmed === story.topic) {
                    setEditingTopic(false);
                    return;
                  }
                  setSavingTopic(true);
                  try {
                    await adminStoryRepository.assignTopic(story.id, trimmed);
                    onTopicUpdate?.(trimmed);
                  } catch (err) {
                    console.error('Failed to assign topic', err);
                  } finally {
                    setSavingTopic(false);
                    setEditingTopic(false);
                  }
                }}
              >
                <input
                  ref={topicInputRef}
                  type="text"
                  value={topicDraft}
                  onChange={(e) => setTopicDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setEditingTopic(false); }}
                  disabled={savingTopic}
                  style={{ fontSize: '0.8rem', padding: '2px 6px', width: 180 }}
                  autoFocus
                />
                <button type="submit" className="ghost-button" disabled={savingTopic} style={{ fontSize: '0.75rem', padding: '2px 6px' }}>
                  {savingTopic ? '...' : 'Save'}
                </button>
                <button type="button" className="ghost-button" onClick={() => setEditingTopic(false)} disabled={savingTopic} style={{ fontSize: '0.75rem', padding: '2px 6px' }}>
                  Cancel
                </button>
                {story.topic && (
                  <button
                    type="button"
                    className="ghost-button"
                    disabled={savingTopic}
                    style={{ fontSize: '0.75rem', padding: '2px 6px', color: STATUS_THEME_COLORS.error, marginLeft: 4 }}
                    onClick={async () => {
                      setSavingTopic(true);
                      try {
                        await adminStoryRepository.unassignTopic(story.id);
                        onTopicUpdate?.('');
                      } catch (err) {
                        console.error('Failed to remove topic', err);
                      } finally {
                        setSavingTopic(false);
                        setEditingTopic(false);
                      }
                    }}
                  >
                    No topic
                  </button>
                )}
              </form>
            ) : story.topic ? (
              <span
                className="admin-topic-badge"
                title={`${story.topic} (click to edit)`}
                style={{ cursor: 'pointer' }}
                onClick={() => { setTopicDraft(story.topic || ''); setEditingTopic(true); }}
              >{story.topic}</span>
            ) : (
              <span
                className="admin-topic-badge admin-topic-badge--empty"
                style={{ cursor: 'pointer' }}
                title="Click to assign a topic"
                onClick={() => { setTopicDraft(''); setEditingTopic(true); }}
              >+ Add topic</span>
            )}
            {changes.length > 0 && (
              <button
                className="ghost-button"
                onClick={() => setChangesOpen((v) => !v)}
                style={{ fontSize: '0.8rem', padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <span style={{ fontSize: '0.7rem' }}>{changesOpen ? '▼' : '▶'}</span>
                {changes.length} change{changes.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}

        {isAdmin && changesOpen && changes.length > 0 && (
          <div className="detail-section" style={{ paddingTop: 0, paddingBottom: 0 }}>
            <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--line)' }}>
                    <td style={{ padding: '4px 8px 4px 0', whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                      {formatRelativeTime(c.created_at)}
                    </td>
                    <td style={{ padding: '4px 8px' }}>{c.event_type}</td>
                    <td style={{ padding: '4px 8px', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }} title={c.agent_id}>
                      {c.agent_id}
                    </td>
                    <td style={{ padding: '4px 0', textAlign: 'right' }}>
                      <Link
                        to={`/admin/change/${c.id}`}
                        className="ghost-button"
                        style={{ fontSize: '0.78rem', padding: '2px 6px' }}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {story.linked_url && (
          <div className="linked-url-row">
            <a href={safeHref(story.linked_url)} target="_blank" rel="noreferrer" className="linked-url-link" onClick={() => recordEvent(story.id, 'click_external')}>
              <IconExternalLink size={11} strokeWidth={2.5} />
              {linkHost(story.linked_url)}
            </a>
          </div>
        )}

        <section className="detail-section">
          <div className="section-heading-row">
            <h3>Summary</h3>
            {summaryText && <CopyButton text={summaryText} size={13} />}
            {isAdmin && (
              <>
                <button
                  className="inspect-section-btn"
                  onClick={() => {
                    setSummaryDraft(summaryText);
                    setEditingSummary(true);
                  }}
                  title="Edit summary"
                >
                  <IconPencil size={13} />
                </button>
                <button className="inspect-section-btn" onClick={() => setInspectModal('summary')} title="Inspect raw HTML">
                  <IconInspect />
                </button>
              </>
            )}
          </div>
          {isAdmin && editingSummary ? (
            <div className="summary-edit-area">
              <textarea
                className="summary-edit-textarea"
                value={summaryDraft}
                onChange={(e) => setSummaryDraft(e.target.value)}
                rows={6}
                autoFocus
              />
              <div className="summary-edit-actions">
                <button
                  className="summary-save-btn"
                  disabled={savingSummary}
                  onClick={async () => {
                    if (!story) return;
                    setSavingSummary(true);
                    try {
                      await adminStoryRepository.updateStory(story.id, { description: summaryDraft });
                      onSummaryUpdate?.(summaryDraft);
                      setEditingSummary(false);
                    } catch {
                      // silently fail
                    } finally {
                      setSavingSummary(false);
                    }
                  }}
                >
                  {savingSummary ? 'Saving…' : 'Save'}
                </button>
                <button
                  className="summary-cancel-btn"
                  disabled={savingSummary}
                  onClick={() => setEditingSummary(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p>{summaryText || 'No summary available.'}</p>
          )}
        </section>
        {story.content_updated_at && (
          <div className="content-updated-notice">
            Content updated by source {formatRelativeTime(story.content_updated_at)}
          </div>
        )}
        <section className="detail-section">
          <div className="section-heading-row">
            <h3>Article</h3>
            {isAdmin && (
              <button className="inspect-section-btn" onClick={() => setInspectModal('content')} title="Inspect raw HTML">
                <IconInspect />
              </button>
            )}
          </div>
          {contentHtml
            ? <div className="detail-content" dangerouslySetInnerHTML={{ __html: contentHtml }} />
            : <div className="detail-content">No content available yet.</div>
          }
        </section>
      </div>

      {isAdmin && inspectModal === 'summary' && (
        <InspectModal title="Summary" html={story.summary || ''} onClose={() => setInspectModal(null)} />
      )}
      {isAdmin && inspectModal === 'content' && (
        <InspectModal title="Content" html={story.content || ''} onClose={() => setInspectModal(null)} />
      )}
    </section>
  );
});
